// NUMBER FORMATTING UTILITY
// Formats numbers to show 1 decimal place only when needed (not for integers)
export const formatNumber = (value) => {
    if (isNaN(value) || value === null || value === undefined) {
        return '';
    }
    const numValue = parseFloat(value);
    // Check if the number is an integer
    if (numValue === Math.floor(numValue)) {
        return numValue.toString();
    } else {
        return numValue.toFixed(1);
    }
};

// Formats numbers for locale string with 1 decimal place only when needed
export const formatNumberWithLocale = (value, options = {}) => {
    if (isNaN(value) || value === null || value === undefined) {
        return '';
    }
    const numValue = parseFloat(value);

    // If a specific locale is provided in options, use it with toLocaleString
    if (options.locale) {
        const { locale, ...restOptions } = options;

        // Check if the number is an integer
        if (numValue === Math.floor(numValue)) {
            return numValue.toLocaleString(locale, {
                ...restOptions,
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            });
        } else {
            return numValue.toLocaleString(locale, {
                ...restOptions,
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
            });
        }
    }

    // Default: Use custom formatting with space as thousands separator and period as decimal separator
    const isInteger = numValue === Math.floor(numValue);
    const formatted = isInteger ? numValue.toFixed(0) : numValue.toFixed(1);

    // Split into integer and decimal parts
    const parts = formatted.split('.');
    const integerPart = parts[0];
    const decimalPart = parts[1];

    // Add thousands separator
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    // Combine with decimal part if it exists
    return decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger;
};

// A logarithmic y axis only helps when the series really do sit on different orders of
// magnitude, and it can only be drawn when every value is strictly positive: log(0) is
// undefined and negatives have no place on the axis at all. Both conditions are checked
// against the data actually on screen, because whether they hold depends on the country -
// meat trade for India spans 480x, for another country it may not.
// Some SDG views flatten disaggregations they do not carry as columns - vw_sdg_full has no
// age and no urban/rural - so the same country, year and series comes back as several rows
// whose only difference is the value: India reports fifteen different female readings for
// 5.4.1 in 2019, all identical on element, source, nature and footnotes. Nothing in the row
// says which one is the 15+/rural figure, so picking one is a guess, and .find() below used
// to take whichever row BigQuery happened to return first. Drop the reading instead of
// drawing an arbitrary one. Rows that agree on the value are kept: those are harmless repeats.
const valuesAgree = (values) => {
    const distinct = new Set(
        values.map((value) => (value === null || value === undefined ? "" : String(value).trim()))
    );
    return distinct.size <= 1;
};

export const LOG_SCALE_RATIO = 20;

export const canUseLogScale = (data) => {
    const series = (data?.datasets ?? []).filter((dataset) => !dataset.noData);
    if (series.length < 2) return false;

    const maxima = [];
    for (const dataset of series) {
        const values = (dataset.data ?? [])
            .map((point) => (point && typeof point === "object" ? point.y : point))
            .map(Number)
            .filter((value) => Number.isFinite(value));
        if (values.some((value) => value <= 0)) return false;
        if (values.length === 0) continue;
        maxima.push(Math.max(...values));
    }

    if (maxima.length < 2) return false;
    return Math.max(...maxima) / Math.min(...maxima) >= LOG_SCALE_RATIO;
};

// Chart.js derives the tick step from the data range: a series sitting around 0.3 gets a step
// of 0.05, and rounding every tick to one decimal prints "0.3" three times in a row. The number
// of decimals is taken from the step instead, so consecutive ticks always read differently.
export const formatAxisValue = (value, ticks) => {
    const step = ticks?.length > 1 ? Math.abs(ticks[1].value - ticks[0].value) : 0;
    if (step > 0 && step < 1) {
        return Number(value).toFixed(Math.min(6, Math.ceil(-Math.log10(step))));
    }
    return formatNumberWithLocale(value);
};

export const matchesCode = (val1, val2) => {
    if (val1 === undefined || val1 === null || val2 === undefined || val2 === null) return false;
    const str1 = String(val1).trim();
    const str2 = String(val2).trim();
    return str1 === str2 || (!isNaN(Number(str1)) && !isNaN(Number(str2)) && Number(str1) === Number(str2));
};

// DATA CONVERSION FOR CHARTJS - Line Chart
export const transformDataLineChart = (data, element_code, item_code, countryCode, sex_code, group_code, isPoU, isFoodInsecurity, indicator_code, use_item_labels, comparingCountries, selectedYears = null, fullInfo = null) => {
    // Determine if there are multiple code combinations (item/element/indicator)
    // Ensure code parameters are arrays to avoid TypeError
    if (!Array.isArray(element_code)) {
        element_code = element_code !== undefined && element_code !== null && element_code !== "" ? [element_code] : [];
    }
    element_code = element_code.filter(c => c !== null && c !== undefined && c !== "").map(c => {
        const s = String(c).trim();
        return isNaN(Number(s)) ? s : Number(s);
    });

    if (!Array.isArray(item_code)) {
        item_code = item_code !== undefined && item_code !== null && item_code !== "" ? [item_code] : [];
    }
    item_code = item_code.filter(c => c !== null && c !== undefined && c !== "").map(c => {
        const s = String(c).trim();
        return isNaN(Number(s)) ? s : Number(s);
    });

    if (!Array.isArray(indicator_code)) {
        indicator_code = indicator_code !== undefined && indicator_code !== null && indicator_code !== "" ? [indicator_code] : [];
    }
    indicator_code = indicator_code.filter(c => c !== null && c !== undefined && c !== "").map(c => {
        const s = String(c).trim();
        return isNaN(Number(s)) ? s : Number(s);
    });
    const hasMultipleCombos = (
        Array.isArray(item_code) && item_code.length > 1
    ) || (
            Array.isArray(element_code) && element_code.length > 1
        ) || (
            Array.isArray(indicator_code) && indicator_code.length > 1
        );

    // Only allow subregional comparison (use all areas) when explicitly comparing countries
    // and there is NOT a multi-code combination. Otherwise, restrict to the selected country.
    const allowSubregionCompare = Boolean(comparingCountries) && !hasMultipleCombos;

    const filteredByArea = allowSubregionCompare
        ? data
        : data.filter(item => Number(item.area_code_m49) === Number(countryCode));

    // One M49 code can carry several names in the SDG views - "Southern Asia", "Southern Asia
    // (excluding intra-trade)" and "Southern Asia, unspecified (population)" are all code 34 -
    // which splits a single region into two or three identical series on the chart. The
    // duplicated rows always hold the same value (checked across every 1.3.1 group in
    // vw_sdg_full: 5940 groups, none with differing values), so they are collapsed onto the
    // shortest name, which is the unqualified one.
    const canonicalAreaName = {};
    filteredByArea.forEach(item => {
        const code = item.area_code_m49;
        if (code === undefined || code === null || !item.area) return;
        if (!canonicalAreaName[code] || item.area.length < canonicalAreaName[code].length) {
            canonicalAreaName[code] = item.area;
        }
    });

    // Renaming alone would leave the duplicates sitting inside a single series as repeated
    // points at the same year, so rows that become indistinguishable once renamed are dropped.
    const seenRows = new Set();
    const countryData = filteredByArea.reduce((rows, item) => {
        const canonical = canonicalAreaName[item.area_code_m49];
        const row = canonical && canonical !== item.area ? { ...item, area: canonical } : item;
        if (canonical) {
            const key = [row.area_code_m49, row.year, row.item_code, row.element_code, row.sex, row.value].join("|");
            if (seenRows.has(key)) return rows;
            seenRows.add(key);
        }
        rows.push(row);
        return rows;
    }, []);


    const yearsSet = new Set();

    // get the number of countries in the data slice
    const totCountries = new Set(countryData.map(item => item.area_code_m49)).size;

    countryData.forEach(item => {
        if (parseInt(item.year) > 2007) {
            yearsSet.add(item.year);
        }
    });

    let labels = Array.isArray(selectedYears) && selectedYears.length > 0
        ? [...selectedYears]
        : Array.from(yearsSet).sort();
    // determine grouping logic based on item_code, element_code, and indicator_code
    let groupByKey;
    if (comparingCountries && item_code.length <= 1 && (!indicator_code || indicator_code.length <= 1) && element_code.length <= 1 && totCountries > 1) {
        groupByKey = "area";
    } else if (indicator_code && indicator_code.length > 0 && countryData.every(item => item.indicator)) {
        // If indicator_code exists, group by indicator
        groupByKey = "indicator";
    } else if ((item_code.length > 1 && countryData.every(item => item.item)) || use_item_labels) {
        groupByKey = "item";
    } else if (item_code.length > 1 && (countryData.every(item => item.item) || countryData.every(item => item.item_name))) {
        groupByKey = "item_name";
    } else if (item_code.length <= 1 && element_code.length > 1 && countryData.every(item => item.element)) {
        groupByKey = "element";
    } else {
        groupByKey = "area";
    }

    const groupData = {};
    const orderedKeys = []; // Track the order of keys as they appear
    let info = null;

    // When grouping by item and multiple item_codes, process in item_code array order
    if (groupByKey === "item" && item_code.length > 1) {
        item_code.forEach(code => {
            // matchesCode, not ===: item_code is normalised to a Number above when it looks
            // numeric, while the views return item_code as a STRING, so a strict comparison
            // matches nothing and the chart comes out empty. Every sibling branch already
            // goes through this helper.
            const itemsForCode = countryData.filter(item => matchesCode(item.item_code, code));
            itemsForCode.forEach(item => {
                if (labels.includes(item.year)) {
                    const groupKey = item[groupByKey] ? item[groupByKey] : use_item_labels ? item['item_description'] : item['item'];

                    if (!groupData[groupKey]) {
                        groupData[groupKey] = { label: groupKey, data: [] };
                        orderedKeys.push(groupKey); // Track order
                    }

                    if (
                        item.value !== "" &&
                        item.value !== null &&
                        item.value !== undefined &&
                        !isNaN(parseFloat(item.value))
                    ) {
                        groupData[groupKey].data.push({
                            x: item.year,
                            y: parseFloat(item.value),
                            flagDescription: item.flag_description || item.FootNote || item.footnote || item.footnotes || "",
                            // Derived, not reported: the previous year with a reading, which for
                            // these indicators is where the period covered by this value starts.
                            derivedPeriodStart: item.derived_period_start,
                            // Only the monthly domains report it. An annual figure is the mean of the
                            // months present, so a year still in progress sits next to complete ones
                            // looking comparable when it is not.
                            monthsObserved: item.months_observed,
                        });
                    }

                    // Collect other info from the first data item
                    if (!info) {
                        const { area, year, value, ...otherInfo } = item;
                        info = otherInfo;
                        if (!info.unit && item.item && item.item.includes("0-100")) {
                            info.unit = "%";
                        }
                    }
                }
            });
        });
    } else if (groupByKey === "element" && element_code.length > 1) {
        // When grouping by element and multiple element_codes, process in element_code array order
        element_code.forEach(code => {
            const itemsForCode = countryData.filter(item => matchesCode(item.element_code, code));
            itemsForCode.forEach(item => {
                if (labels.includes(item.year)) {
                    const groupKey = item[groupByKey];

                    if (!groupData[groupKey]) {
                        groupData[groupKey] = { label: groupKey, data: [] };
                        orderedKeys.push(groupKey); // Track order
                    }

                    if (
                        item.value !== "" &&
                        item.value !== null &&
                        item.value !== undefined &&
                        !isNaN(parseFloat(item.value))
                    ) {
                        groupData[groupKey].data.push({
                            x: item.year,
                            y: parseFloat(item.value),
                            flagDescription: item.flag_description || item.FootNote || item.footnote || item.footnotes || "",
                            // Derived, not reported: the previous year with a reading, which for
                            // these indicators is where the period covered by this value starts.
                            derivedPeriodStart: item.derived_period_start,
                            // Only the monthly domains report it. An annual figure is the mean of the
                            // months present, so a year still in progress sits next to complete ones
                            // looking comparable when it is not.
                            monthsObserved: item.months_observed,
                        });
                    }

                    // Collect other info from the first data item
                    if (!info) {
                        const { area, year, value, ...otherInfo } = item;
                        info = otherInfo;
                        if (!info.unit && item.item && item.item.includes("0-100")) {
                            info.unit = "%";
                        }
                    }
                }
            });
        });
    } else {
        // Default: process in the order data appears
        countryData.forEach(item => {
            if (labels.includes(item.year)) {
                const groupKey = item[groupByKey] ? item[groupByKey] : use_item_labels ? item['item_description'] : item['item'];

                if (!groupData[groupKey]) {
                    groupData[groupKey] = { label: groupKey, data: [] };
                    orderedKeys.push(groupKey); // Track order
                }

                if (
                    item.value !== "" &&
                    item.value !== null &&
                    item.value !== undefined &&
                    !isNaN(parseFloat(item.value))
                ) {
                    groupData[groupKey].data.push({
                        x: item.year,
                        y: parseFloat(item.value),
                        flagDescription: item.flag_description || item.FootNote || item.footnote || item.footnotes || "",
                        // Derived, not reported: the previous year with a reading, which for
                        // these indicators is where the period covered by this value starts.
                        derivedPeriodStart: item.derived_period_start,
                        // Only the monthly domains report it. An annual figure is the mean of the
                        // months present, so a year still in progress sits next to complete ones
                        // looking comparable when it is not.
                        monthsObserved: item.months_observed,
                    });
                }

                // Collect other info from the first data item
                if (!info) {
                    const { area, year, value, ...otherInfo } = item;
                    info = otherInfo;
                    if (!info.unit && item.item && item.item.includes("0-100")) {
                        info.unit = "%";
                    }
                }
            }
        });
    }


    // Collapse rows sharing a year before anything downstream sees them: otherwise the
    // duplicates above also blow past the 15-point cap below and truncate real years away.
    Object.keys(groupData).forEach(key => {
        const byYear = new Map();
        groupData[key].data.forEach(point => {
            if (!byYear.has(point.x)) byYear.set(point.x, []);
            byYear.get(point.x).push(point);
        });
        groupData[key].data = [...byYear.values()]
            .filter(points => valuesAgree(points.map(point => point.y)))
            .map(points => points[0]);
    });

    // restrict data points to 15 per group (UI default shows last 15 years)
    Object.keys(groupData).forEach(key => {
        if (groupData[key].data.length > 15) {
            groupData[key].data = groupData[key].data.slice(-15); // Keep only the last 15 data points
        }

        groupData[key].data.sort((a, b) => parseInt(a.x) - parseInt(b.x));
    });

    // const backgroundColors = [
    //     '#ff0090', '#ffcc00', '#9900cc', '#58a14e',
    //     '#478ed5', '#ff9966', '#66cccc', '#ff62c0',
    //     "#66ff66", "#c33fb0f66", "#2700ff", "#9966ff", "#ffcc99",
    // ];
    const backgroundColors = [
        "#368dff", "#c53b3b", "#eeb600", "#00bf8e",
        "#8e5ea2", '#ff009f', "#2700ff", "#cfca00",
        "#027602ff", "#ff00ff", "#22a97cff", "#9966ff", "#ffcc99",
    ]

    const worldColor = "#00bf8e";

    // Dedicated colors for continent-level comparisons, so they stay distinct and
    // identifiable instead of being pulled from the generic country color pool
    // (mirrors the specialRegionColors map already used in transformDataMultipleAxis).
    const specialRegionColors = {
        'Africa': '#D2691E',
        'Asia': '#4682B4',
        'Europe': '#9370DB',
        'Northern America': '#20B2AA',
        'South America': '#228B22',
        'Oceania': '#FF6347'
    };

    const datasets = [];

    // track used colors to avoid repeating them unnecessarily
    let usedColors = new Set();

    // When set on the indicator (e.g. SPI overall score vs. pillar sub-scores), make the
    // first series (index 0, i.e. item_code[0]) visually stand out with a square marker,
    // and mute the remaining series with a lighter tint of their assigned color.
    const highlightPrimarySeries = Boolean(fullInfo?.highlightPrimarySeries);
    const lightenHexColor = (hex, amount = 0.55) => {
        const match = hex.replace(/^#/, '').match(/.{2}/g);
        if (!match) return hex;
        const [r, g, b] = match.map(x => parseInt(x, 16));
        const lighten = c => Math.round(c + (255 - c) * amount);
        return `#${[lighten(r), lighten(g), lighten(b)].map(c => c.toString(16).padStart(2, '0')).join('')}`;
    };

    // Create datasets in the order they were encountered
    orderedKeys.forEach((key, index) => {
        const group = groupData[key];
        const validData = group.data.filter(dataPoint => !isNaN(dataPoint.y) && dataPoint.y !== null);

        // Some indicators (e.g. rate-of-change metrics computed over multi-year spans) report
        // data points several years apart instead of annually. When consecutive points in a
        // series are more than 1 year apart, tag the later point with the actual period it
        // covers (e.g. "2015-2025") so the axis/tooltip can show that instead of just the
        // bare end year - there's no separate "time detail" field in the source data, so this
        // is derived from the real gap between reported years.
        // Opt-in per indicator, and always a derivation: no source publishes the reference
        // period, but for a change rate measured between two assessments the span since the
        // previous reading is the period by definition. Inferring it from any gap would mislabel
        // sparse point-in-time series - SDG 1.3.1 for India reports 2015, 2016, 2019 and 2022,
        // and those are single-year estimates, not ranges - hence the flag.
        if (fullInfo?.reports_period) validData.forEach((point, i) => {
            // For the first point the previous reading is usually outside the selected range:
            // 15.2.1 for India reports 2000, 2015 and 2025, so with the default 2015-2024 range
            // 2015 is the only point on the chart and had nothing to compare against. The query
            // carries the previous reported year for exactly this case.
            const prevYear = parseInt(i === 0 ? point.derivedPeriodStart : validData[i - 1].x, 10);
            const thisYear = parseInt(point.x, 10);
            if (!isNaN(prevYear) && !isNaN(thisYear) && thisYear - prevYear > 1) {
                point.timeDetail = `${prevYear}-${thisYear}`;
            }
        });

        if (validData.length > 0) {
            let color = backgroundColors.find(c => !usedColors.has(c));

            if (group.label === "World") {
                color = worldColor;
            } else if (specialRegionColors[group.label]) {
                color = specialRegionColors[group.label];
            }

            if (!color) {
                color = backgroundColors[index % backgroundColors.length];
            }

            usedColors.add(color);

            const isPrimarySeries = highlightPrimarySeries && index === 0;
            const displayColor = highlightPrimarySeries && !isPrimarySeries ? lightenHexColor(color) : color;

            const dataset = {
                label: group.label,
                data: validData,
                borderColor: displayColor,
                backgroundColor: displayColor,
                ...(isPrimarySeries ? { pointStyle: 'rect', pointRadius: 4, borderWidth: 3 } : {}),
            };

            datasets.push(dataset);
        }
    });

    // Map each x-axis label (year) to the derived "2015-2025"-style period computed above,
    // so axis ticks/tooltips can show it. Falls back to nothing (plain year) when the gap
    // between consecutive points is 1 year or less.
    const periodByYear = {};
    datasets.forEach(ds => {
        ds.data.forEach(point => {
            if (point?.timeDetail && !periodByYear[point.x]) {
                periodByYear[point.x] = point.timeDetail;
            }
        });
    });

    // Allow mockData.js to force a unit when the underlying view doesn't provide one,
    // so the data table's Unit column isn't left blank.
    if (fullInfo?.unit) {
        info = { ...(info || {}), unit: fullInfo.unit };
    }

    return { labels, datasets, info, groupByKey, periodByYear };
};

// DATA CONVERSION FOR CHARTJS - Pyramid Chart
export const transformDataPyramid = (data, _element_code, _item_code, _countryCode, _sex_code, _group_code, _isPoU, _isFoodInsecurity, _indicator_code, _unused = null, _unused2 = null, _selectedYears = null, fullInfo = null) => {


    const allYears = Array.from(new Set(data.map(item => parseInt(item.year)))).filter(Boolean);
    const latestYear = Math.max(...allYears);

    // filter data to only the latest year
    const filteredData = data.filter(item => parseInt(item.year) === latestYear);

    const allAgeGroups = Array.from(new Set(filteredData.map(item => item.age || item.item)));
    const sortedAgeGroups = allAgeGroups.sort((a, b) => {
        const aStart = parseInt(a.split("-")[0]) || 0;
        const bStart = parseInt(b.split("-")[0]) || 0;
        return bStart - aStart;
    });

    const datasets = [
        {
            label: `Male`,
            stack: `year-${latestYear}`,
            backgroundColor: "#478ed5",
            data: sortedAgeGroups.map(age => {
                const entry = filteredData.find(d => (d.age || d.item) === age && d.sex_code === "1");
                return entry ? entry.value : 0;
            }),
        },
        {
            label: `Female`,
            stack: `year-${latestYear}`,
            backgroundColor: "#8200e5",
            data: sortedAgeGroups.map(age => {
                const entry = filteredData.find(d => (d.age || d.item) === age && d.sex_code === "2");
                return entry ? -entry.value : 0;
            }),
        },
    ];

    const totalMale = datasets[0].data.reduce((sum, val) => sum + Math.abs(val), 0);
    const totalFemale = datasets[1].data.reduce((sum, val) => sum + Math.abs(val), 0);

    let info = null;
    if (filteredData.length > 0) {
        const { Area, Year, Value, ...otherInfo } = filteredData[0];
        info = { ...otherInfo, year: latestYear };
    }

    // Allow mockData.js to force a unit when the underlying view doesn't provide one,
    // so the data table's Unit column isn't left blank.
    if (fullInfo?.unit) {
        info = { ...(info || {}), unit: fullInfo.unit };
    }

    return {
        labels: sortedAgeGroups,
        datasets,
        info,
        // Read by the data table and its download to pick the pyramid layout. They used to infer
        // it from an age_code on the first row, which every vw_unpd row carries - so the net
        // migration line charts were laid out as a pyramid, with "Population" as their unit and
        // every value cell blank.
        isPyramid: true,
        latestYear,
        totalMale,
        totalFemale,
        rawData: data // Include raw data for time controls
    };
};



// DATA CONVERSION FOR CHARTJS - StackedBars
export const transformDataStackedBar = (data, element_code, item_code, countryCode, sex_code, group_code, isPoU, isFoodInsecurity, indicator_code, _unused = null, _unused2 = null, selectedYears = null, fullInfo = null) => {

    const countryData = data.filter(item => Number(item.area_code_m49) === Number(countryCode));

    const yearsSet = new Set();
    const areasSet = new Set();

    // Ensure element_code is a clean array
    if (!Array.isArray(element_code)) {
        element_code = element_code !== undefined && element_code !== null && element_code !== "" ? [element_code] : [];
    }
    element_code = element_code.filter(code => code !== null && code !== undefined && code !== "").map(code => {
        const str = String(code).trim();
        return isNaN(Number(str)) ? str : Number(str);
    });

    // Ensure item_code is a clean array
    if (!Array.isArray(item_code)) {
        item_code = item_code !== undefined && item_code !== null && item_code !== "" ? [item_code] : [];
    }
    item_code = item_code.filter(code => code !== null && code !== undefined && code !== "").map(code => {
        const str = String(code).trim();
        return isNaN(Number(str)) ? str : Number(str);
    });

    // Filter data based on element_code and exclude null/undefined values
    const filteredData = countryData.filter(item => {
        const hasCode = element_code.length === 0 || element_code.some(code => matchesCode(item.element_code, code));
        return hasCode && item.value !== null && item.value !== undefined && !isNaN(item.value);
    });

    // Collect years and areas
    filteredData.forEach(item => {
        yearsSet.add(item.year);
        areasSet.add(item.area);
    });


    const years = Array.isArray(selectedYears) && selectedYears.length > 0
        ? [...selectedYears]
        : Array.from(yearsSet).sort();
    const areas = Array.from(areasSet);

    let info = null;
    if (filteredData.length > 0) {
        const { Area, Year, Value, ...otherInfo } = filteredData[0];
        info = otherInfo;
    }

    // If no valid data after filtering, return empty structure
    if (filteredData.length === 0) {
        return { labels: [], datasets: [], info: info || {}, areas: [] };
    }

    // Define subcategory labels by combining Years and Areas
    const labels = years.flatMap(year => areas.map(area => [year, area]));

    // const backgroundColors = [
    //     "#478ed5", "#8200e5", "#188986", "#00ccff",
    //     "#58a14e", "#ff6600", "#ff9f41", "#ff33cc", "#FFCE1B", "#5d76cb", "#004242", "#627500", "#b71818", "#a69b04",
    // ];

    const backgroundColors = [
        "#368dff", "#c53b3b", "#eeb600", "#00bf8e",
        "#8e5ea2", '#ff009f', "#2700ff", "#cfca00",
        "#027602ff", "#ff00ff", "#22a97cff", "#9966ff", "#ffcc99",
    ]

    const itemColorMap = {};
    let colorIndex = 0;

    const itemData = {};
    const orderedKeys = []; // Track the order of keys as they appear
    const suffixes = ['-M', '-F', '_T'];

    // Handle multiple item_codes - group by item similar to transformDataBarChart
    if (Array.isArray(item_code) && item_code.length > 1) {
        // Process in the order of item_code array
        item_code.forEach(code => {
            const itemsForCode = filteredData.filter(item => matchesCode(item.item_code, code));
            itemsForCode.forEach(item => {
                const key = item.item; // Use item as the key when multiple item_codes
                const suffix = suffixes.find(suffix => typeof item.item_code === 'string' && item.item_code.endsWith(suffix)) || null;

                if (!itemData[key]) {
                    itemData[key] = { label: key, data: [], suffix };
                    orderedKeys.push(key); // Track order
                    itemColorMap[key] = backgroundColors[colorIndex % backgroundColors.length];
                    colorIndex++;
                }

                itemData[key].data.push({
                    year: item.year,
                    area: item.area,
                    value: item.value,
                    flagDescription: item.flag_description || item.FootNote || item.footnote || item.footnotes || ""
                });
            });
        });
    } else if (element_code.length > 1) {
        // Process in the order of element_code array
        element_code.forEach(code => {
            const itemsForCode = filteredData.filter(item => matchesCode(item.element_code, code));
            itemsForCode.forEach(item => {
                const key = item.element;
                const suffix = suffixes.find(suffix => typeof item.item_code === 'string' && item.item_code.endsWith(suffix)) || null;

                if (!itemData[key]) {
                    itemData[key] = { label: key, data: [], suffix };
                    orderedKeys.push(key); // Track order
                    itemColorMap[key] = backgroundColors[colorIndex % backgroundColors.length];
                    colorIndex++;
                }

                itemData[key].data.push({
                    year: item.year,
                    area: item.area,
                    value: item.value,
                    flagDescription: item.flag_description || item.FootNote || item.footnote || item.footnotes || ""
                });
            });
        });
    } else {
        // Default: process by food_group or item
        filteredData.forEach(item => {
            const key = group_code?.length > 1 ? item.food_group : item.item;
            const suffix = suffixes.find(suffix => typeof item.item_code === 'string' && item.item_code.endsWith(suffix)) || null;

            if (!itemData[key]) {
                itemData[key] = { label: key, data: [], suffix };
                orderedKeys.push(key); // Track order
                itemColorMap[key] = backgroundColors[colorIndex % backgroundColors.length];
                colorIndex++;
            }

            itemData[key].data.push({
                year: item.year,
                area: item.area,
                value: item.value,
                flagDescription: item.flag_description || item.FootNote || item.footnote || item.footnotes || ""
            });
        });
    }

    const datasets = [];

    // Create datasets in the order they were encountered
    orderedKeys.forEach((key, itemIndex) => {

        const data = labels.map(([year, area]) => {
            const matches = itemData[key].data.filter(d => d.year === year && d.area === area);
            if (matches.length === 0) return 0;
            // null rather than 0: a zero bar reads as a real "0%" reading.
            return valuesAgree(matches.map(d => d.value)) ? matches[0].value : null;
        });

        const flagDescriptions = labels.map(([year, area]) => {
            const dataPoint = itemData[key].data.find(d => d.year === year && d.area === area);
            return dataPoint ? dataPoint.flagDescription : "";
        });

        const dataset = {
            label: itemData[key].label,
            data: data,
            flagDescriptions: flagDescriptions,
            borderColor: itemColorMap[key] || '#000',
            backgroundColor: itemColorMap[key] || '#000',
            stack: itemData[key].suffix ? `Stack ${itemData[key].suffix}` : 'Stack 0',
        };

        datasets.push(dataset);
    });

    // Allow mockData.js to force a unit when the underlying view doesn't provide one,
    // so the data table's Unit column isn't left blank.
    if (fullInfo?.unit) {
        info = { ...(info || {}), unit: fullInfo.unit };
    }

    return { labels, datasets, info, areas };
};

// DATA CONVERSION FOR CHARTJS - bar Chart
// Signature aligned with fetchData call order:
// (data, element_code, item_code, countryCode, sex_code, group_code, isPoU, isFoodInsecurity, indicator_code, use_item_labels, comparingCountries, selectedYears)
// Not every view carries the sex label next to the code: vw_oer and vw_oea have sex_code
// only, so a series keyed on the label alone collapses male and female into one series called
// "undefined". The codes follow FAOSTAT's convention (1 total, 2 male, 3 female; the SDMX
// views spell them out) and the raw code is the last resort, never undefined.
const SEX_LABELS = {
    "1": "Total", "2": "Male", "3": "Female",
    "T": "Total", "M": "Male", "F": "Female",
    "_T": "Total", "SEX_T": "Total", "SEX_M": "Male", "SEX_F": "Female",
};
export const sexLabelFor = (row) =>
    row?.sex || SEX_LABELS[String(row?.sex_code)] || (row?.sex_code != null ? String(row.sex_code) : "");

export const transformDataBarChart = (data, element_code, item_code, countryCode, sex_code, group_code, isPoU, isFoodInsecurity, indicator_code, _unused = null, _unused2 = null, selectedYears = null, fullInfo = null) => {
    // One place deciding how a series is named when the view has no `element` column:
    // an explicit label from mockData.js if there is one, otherwise the code itself.
    const seriesLabelFor = (code) => fullInfo?.label_by_element?.[code] ?? String(code);
    const countryData = Array.isArray(item_code) && item_code.length > 1 ? data.filter(item => Number(item.area_code_m49) === Number(countryCode)) : data.filter(item => Number(item.area_code_m49) === Number(countryCode));

    const yearsSet = new Set();
    const areasSet = new Set();

    // Ensure element_code is a clean array
    if (!Array.isArray(element_code)) {
        element_code = element_code !== undefined && element_code !== null && element_code !== "" ? [element_code] : [];
    }
    element_code = element_code.filter(code => code !== null && code !== undefined && code !== "").map(code => {
        const str = String(code).trim();
        return isNaN(Number(str)) ? str : Number(str);
    });

    // Ensure sex_code is an array
    if (!Array.isArray(sex_code)) {
        sex_code = [sex_code];
    }


    // Filter data based on element_code and exclude null/undefined values
    const filteredData = countryData.filter(item => {
        const hasCode = element_code.length === 0 || element_code.some(code => matchesCode(item.element_code, code));
        return hasCode && item.value !== null && item.value !== undefined && !isNaN(item.value);
    });


    // Collect years and areas
    filteredData.forEach(item => {
        yearsSet.add(item.year);
        areasSet.add(item.area);
    });

    const years = Array.isArray(selectedYears) && selectedYears.length > 0
        ? [...selectedYears]
        : Array.from(yearsSet).sort();
    const areas = Array.from(areasSet);

    let info = null;
    if (filteredData.length > 0) {
        const { Area, Year, Value, ...otherInfo } = filteredData[0];
        info = otherInfo;
    }

    // Allow mockData.js to force a unit when the underlying view doesn't provide one
    if (fullInfo?.unit) {
        info = { ...(info || {}), unit: fullInfo.unit };
    }

    // If no valid data after filtering, return empty structure
    if (filteredData.length === 0) {
        return { labels: [], datasets: [], info: info || {}, areas: [] };
    }

    // Define subcategory labels by combining Years and Areas
    const labels = years.flatMap(year => areas.map(area => [year, area]));

    // const backgroundColors = [
    //     '#478ed5', '#c33fb0', '#ff9966', '#1ed8ca',
    //     '#8200e5', "#ff6600", "#33ccff",
    //     "#ccffcc", "#ff00ff", "#66ffcc", "#9966ff",
    // ];

    const backgroundColors = [
        "#368dff", "#c53b3b", "#eeb600", "#00bf8e",
        "#8e5ea2", '#ff009f', "#2700ff", "#cfca00",
        "#027602ff", "#ff00ff", "#22a97cff", "#9966ff", "#ffcc99",
    ]

    const itemColorMap = {};
    let colorIndex = 0;

    const itemData = {};
    const orderedKeys = []; // Track the order of keys as they appear

    // Handle multiple item_codes - group by item similar to transformDataLineChart
    if (Array.isArray(item_code) && item_code.length > 1) {
        // Process in the order of item_code array
        item_code.forEach(code => {
            // Same type mismatch as the line chart: normalised Number here, STRING in the row.
            const itemsForCode = filteredData.filter(item => matchesCode(item.item_code, code));
            itemsForCode.forEach(item => {
                // Fall back to the item_code itself when the API row has no human-readable
                // "item" name, so series don't silently collapse into a single "undefined" key.
                const key = item.item ?? code;
                const label = item.item ?? fullInfo?.label_by_item?.[code] ?? code;

                if (!itemData[key]) {
                    itemData[key] = { label: label, data: [] };
                    orderedKeys.push(key); // Track order
                    itemColorMap[key] = backgroundColors[colorIndex % backgroundColors.length];
                    colorIndex++;
                }

                itemData[key].data.push({ year: item.year, area: item.area, value: item.value, flagDescription: item.flag_description || item.FootNote || item.footnote || item.footnotes || "" });
            });
        });
    }
    // Decide processing mode based on `sex_code` length
    else if (Array.isArray(sex_code) && sex_code[0]) {
        filteredData.forEach(item => {
            if (!sex_code.includes(item.sex_code)) return; // filter by sex_code

            // if multiple element codes, combine sex_code and element_code for the key
            const sex = sexLabelFor(item);
            const key = element_code.length > 1
                ? `${sex}_${item.element_code}`  // use sex_code and element_code
                : `${item.item}_${sex}`;  // use item and sex if only one element_code

            const label = element_code.length > 1
                ? `${sex} - ${item.element_code}`  // combine sex_code and element for label if multiple element codes
                : sex_code.length === 1 && sex_code[0] === "N" // special case for 'N' (not specified)
                    ? `${item.indicator}`  // use indicator if only one element_code
                    : sex;  // just sex for label if only one element_code

            if (!itemData[key]) {
                itemData[key] = {
                    label: label,
                    data: [],
                };
                orderedKeys.push(key); // Track order
                itemColorMap[key] = backgroundColors[colorIndex % backgroundColors.length];
                colorIndex++;
            }

            itemData[key].data.push({ year: item.year, area: item.area, value: item.value, flagDescription: item.flag_description || item.FootNote || item.footnote || item.footnotes || "" });
        });
    } else {
        // process data by `item` only, respecting element_code order when applicable
        if (element_code.length > 0) {
            // Process in the order of element_code array
            element_code.forEach(code => {
                const itemsForCode = filteredData.filter(item => matchesCode(item.element_code, code));

                if (itemsForCode.length === 0) {
                    // No data at all for this element: still seed an empty series instead of
                    // silently dropping it, so the table row remains visible with blank data
                    // (needed downstream, e.g. by the report generator, to report "no data"
                    // rather than ignore the parameter entirely).
                    const emptyKey = seriesLabelFor(code);
                    if (!itemData[emptyKey]) {
                        itemData[emptyKey] = { label: emptyKey, data: [] };
                        orderedKeys.push(emptyKey);
                        itemColorMap[emptyKey] = backgroundColors[colorIndex % backgroundColors.length];
                        colorIndex++;
                    }
                    return;
                }

                itemsForCode.forEach(item => {
                    // Not every view exposes an `element` column (the UNESCO ones carry only
                    // element_code), and without this the series reached the legend labelled
                    // "undefined". Falls back to the same name an empty series would get, so a
                    // series keeps one label whether or not the selected country has data.
                    const key = item.element ?? seriesLabelFor(item.element_code);

                    if (!itemData[key]) {
                        itemData[key] = { label: key, data: [] };
                        orderedKeys.push(key); // Track order
                        itemColorMap[key] = backgroundColors[colorIndex % backgroundColors.length];
                        colorIndex++;
                    }

                    itemData[key].data.push({ year: item.year, area: item.area, value: item.value, flagDescription: item.flag_description || item.FootNote || item.footnote || item.footnotes || "" });
                });
            });
        } else {
            filteredData.forEach(item => {
                const key = item.item;

                if (!itemData[key]) {
                    itemData[key] = { label: key, data: [] };
                    orderedKeys.push(key); // Track order
                    itemColorMap[key] = backgroundColors[colorIndex % backgroundColors.length];
                    colorIndex++;
                }

                itemData[key].data.push({ year: item.year, area: item.area, value: item.value, flagDescription: item.flag_description || item.FootNote || item.footnote || item.footnotes || "" });
            });
        }
    }

    const datasets = [];

    // Create datasets in the order they were encountered
    orderedKeys.forEach(key => {
        const data = labels.map(([year, area]) => {
            const matches = itemData[key].data.filter(d => d.year === year && d.area === area);
            if (matches.length === 0) return 0;
            // null rather than 0: a zero bar reads as a real "0%" reading.
            return valuesAgree(matches.map(d => d.value)) ? matches[0].value : null;
        });

        const flagDescriptions = labels.map(([year, area]) => {
            const dataPoint = itemData[key].data.find(d => d.year === year && d.area === area);
            return dataPoint ? dataPoint.flagDescription : "";
        });

        const dataset = {
            label: itemData[key].label, // Label includes `sex` if applicable
            data: data,
            flagDescriptions: flagDescriptions,
            borderColor: itemColorMap[key] || '#000',
            backgroundColor: itemColorMap[key] || '#000',
            // Seeded above for an element the country has no reading for. The series stays
            // here so the table row and the report generator still see the parameter, but the
            // chart legend must not advertise a series that draws nothing. Flagged rather than
            // inferred from the values: missing points are padded with 0, so a genuine zero
            // would otherwise be hidden too.
            noData: itemData[key].data.length === 0,
        };

        datasets.push(dataset);
    });

    return { labels, datasets, info, areas };
};


export const transformDataPie = (data, element_code, item_code, countryCode, sex_code, group_code, isPoU, isFoodInsecurity, indicator_code, _unused = null, _unused2 = null, _selectedYears = null, fullInfo = null) => {
    const countryData = Array.isArray(data)
        ? data.filter((row) => Number(row.area_code_m49) === Number(countryCode))
        : [];

    if (!Array.isArray(element_code)) {
        element_code = element_code !== undefined && element_code !== null && element_code !== "" ? [element_code] : [];
    }
    element_code = element_code.filter(code => code !== null && code !== undefined && code !== "").map(code => {
        const str = String(code).trim();
        return isNaN(Number(str)) ? str : Number(str);
    });

    const filtered = countryData.filter((row) =>
        element_code.length === 0 || element_code.some(code => matchesCode(row.element_code, code))
    );

    // pick the latest year present in the filtered slice
    const years = filtered
        .map((r) => parseInt(r.year, 10))
        .filter((y) => Number.isFinite(y));
    const latestYear = years.length ? Math.max(...years) : null;

    // if no data or no year, return empty dataset
    if (!latestYear) {
        return {
            labels: [],
            datasets: [{ label: "", data: [], backgroundColor: [], borderColor: [] }],
            info: { latestYear: null, totalValue: null, totalLabel: null },
        };
    }

    const palette = [
        "#478ed5", "#8200e5", "#ff33cc", "#00ccff",
        "#58a14e", "#ff6600", "#ff9f41", "#188986", "#FFCE1B",
        "#5d76cb", "#004242", "#627500", "#b71818", "#a69b04",
        "#66cccc", "#ff62c0", "#9966ff", "#ffcc99", "#33ccff",
    ];

    const sums = {};
    const colorMap = {};
    const orderedKeys = []; // Track the order of keys as they appear
    let colorIndex = 0;
    let info = null;

    // Handle multiple item_codes - process in order
    if (item_code.length > 1) {
        item_code.forEach(code => {
            const itemsForCode = filtered.filter(row =>
                row.item_code === code && parseInt(row.year, 10) === latestYear
            );
            itemsForCode.forEach(row => {
                if (!info) {
                    const { area, year, value, ...otherInfo } = row;
                    info = otherInfo;
                }

                const key = row.item;
                const val = parseFloat(row.value);
                if (!Number.isFinite(val)) return;

                if (!sums[key]) {
                    sums[key] = 0;
                    orderedKeys.push(key); // Track order
                }
                sums[key] += val;

                if (!colorMap[key]) {
                    colorMap[key] = palette[colorIndex % palette.length];
                    colorIndex++;
                }
            });
        });
    } else if (element_code.length > 1) {
        // Process in the order of element_code array
        element_code.forEach(code => {
            const itemsForCode = filtered.filter(row =>
                matchesCode(row.element_code, code) && parseInt(row.year, 10) === latestYear
            );
            itemsForCode.forEach(row => {
                if (!info) {
                    const { area, year, value, ...otherInfo } = row;
                    info = otherInfo;
                }

                const key = row.element;
                const val = parseFloat(row.value);
                if (!Number.isFinite(val)) return;

                if (!sums[key]) {
                    sums[key] = 0;
                    orderedKeys.push(key); // Track order
                }
                sums[key] += val;

                if (!colorMap[key]) {
                    colorMap[key] = palette[colorIndex % palette.length];
                    colorIndex++;
                }
            });
        });
    } else {
        // Default: process by food_group or item
        filtered.forEach((row) => {
            // keep only records from the latest year
            if (parseInt(row.year, 10) !== latestYear) return;

            if (!info) {
                const { area, year, value, ...otherInfo } = row;
                info = otherInfo;
            }

            // grouping key:
            // - if groups are requested => split by food_group
            // - else => split by item
            const key = Array.isArray(group_code) && group_code.length > 0 ? row.food_group : row.item;

            const val = parseFloat(row.value);
            if (!Number.isFinite(val)) return;

            if (!sums[key]) {
                sums[key] = 0;
                orderedKeys.push(key); // Track order
            }
            sums[key] += val;

            if (!colorMap[key]) {
                colorMap[key] = palette[colorIndex % palette.length];
                colorIndex++;
            }
        });
    }

    // Find and remove the largest value (total) while preserving order
    if (orderedKeys.length === 0) {
        return {
            labels: [],
            datasets: [{ label: "", data: [], backgroundColor: [], borderColor: [] }],
            info: { ...(info || {}), latestYear, totalValue: null, totalLabel: null },
        };
    }

    // Create entries array in the order of orderedKeys
    const entries = orderedKeys.map(key => [key, sums[key]]);

    // Sort by value descending to get the largest
    const sortedEntries = [...entries].sort((a, b) => b[1] - a[1]);
    const [totalLabel, totalValue] = sortedEntries[0];

    // Remove the largest entry from the ordered data
    const remainingEntries = entries.filter(([key]) => key !== totalLabel);

    const sumRemaining = remainingEntries.reduce((sum, [, val]) => sum + val, 0);
    const diff = totalValue - sumRemaining;

    // Add a residual "Others" group if there is a significant remainder and the flag is set
    if (fullInfo?.calculate_residual && totalValue > 0 && diff > (totalValue * 0.005)) {
        const residualLabel = fullInfo?.residual_label || "Others";
        remainingEntries.push([residualLabel, diff]);
        colorMap[residualLabel] = "#B0B0B0"; // Grey for "Others"
    }

    const labels = remainingEntries.map(([key]) => [key, ""]);
    const values = remainingEntries.map(([, value]) => value);
    const colors = remainingEntries.map(([key]) => colorMap[key]);

    // Allow mockData.js to force a unit when the underlying view doesn't provide one,
    // so the data table's Unit column isn't left blank.
    if (fullInfo?.unit) {
        info = { ...(info || {}), unit: fullInfo.unit };
    }

    return {
        labels,
        datasets: [
            {
                label: info?.unit || "",
                data: values,
                backgroundColor: colors,
                borderColor: colors,
            },
        ],
        info: {
            ...(info || {}),
            latestYear,
            totalValue,
            totalLabel
        },
    };
};



// DATA CONVERSION FOR CHARTJS - Multiple Axis charts
// Signature aligned with fetchData call order & keeps comparingCountries placeholder (unused here)
export const transformDataMultipleAxis = (data, element_code, item_code, countryCode, sex_code, group_code, PoU, isFoodSecurity, indicator_code, useSideBySideBars = false, _comparingCountries = null, selectedYears = null, fullInfo = null) => {
    const isPoU = PoU === true;
    const isFS = isFoodSecurity === true;

    const yearsSet = new Set();
    const compareYearsSet = new Set();
    const groupByKey = "area";

    // Define color schemes for alternating datasets
    const backgroundColors = [
        "#368dff", "#c53b3b", "#eeb600", "#00b284",
        "#8e5ea2", '#ff009f', "#2700ff", "#cfca00",
        "#027602ff", "#ff00ff", "#22a97cff", "#9966ff", "#ffcc99",
    ];

    // Extended color palette for regional comparisons with better contrast and accessibility
    const regionalColors = [
        '#2E8B57', '#FF6347', '#4682B4', '#D2691E',
        '#9370DB', '#20B2AA', '#CD853F', '#DC143C',
        '#008B8B', '#B22222', '#228B22', '#FF1493',
        '#00CED1', '#FF4500', '#9932CC', '#32CD32'
    ];

    // Special colors for specific regions
    const specialRegionColors = {
        'World': '#58a14e',
        'Africa': '#D2691E',
        'Asia': '#4682B4',
        'Europe': '#9370DB',
        'Northern America': '#20B2AA',
        'South America': '#228B22',
        'Oceania': '#FF6347'
    };

    // Function to get distinctive colors avoiding conflicts
    const getDistinctiveColor = (index, isRegional = false) => {
        if (isRegional) {
            return regionalColors[index % regionalColors.length];
        } else {
            return backgroundColors[index % backgroundColors.length];
        }
    };

    // Exclusion reasons mapping
    const exclusionReasons = {
        'pou_low': 'Value below 2.5% threshold',
        'fs_percent_low': 'Value below 0.5% threshold',
        'fs_number_low': 'Value below 100 (1000 No) threshold'
    };

    let info = null;



    // const countryData = data.filter(item => item.area_code_m49 === countryCode);
    // Filter to include only items with valid values (not null, undefined, or NaN)
    const countryData = data.filter(item =>
        Number(item.area_code_m49) === Number(countryCode) &&
        item.value !== null &&
        item.value !== undefined &&
        !isNaN(item.value)
    );


    // Collect unique years for x-axis labels
    countryData.forEach(item => yearsSet.add(item.year));
    const labels = Array.isArray(selectedYears) && selectedYears.length > 0
        ? [...selectedYears]
        : Array.from(yearsSet).sort();

    // Initialize excluded array with nulls for each label
    const excluded = labels.map(() => null);

    // Determine if we're working with multiple item_codes (take priority) or multiple element_codes
    const useMultipleItemCodes = Array.isArray(item_code) && item_code.length > 1;

    // Check if the bars should be stacked (unless useSideBySideBars is true)
    const shouldStack = !useSideBySideBars && Array.isArray(item_code) && item_code.length > 3;

    // Pair item_code and element_code arrays for dataset creation
    const combinedCodes = useMultipleItemCodes
        ? item_code.map((code, index) => ({ item_code: code, element_code: element_code[index] || element_code[0] }))
        : element_code.map((code, index) => ({ item_code: item_code[0], element_code: code }));



    // Create datasets based on combined item_code and element_code entries
    const datasets = combinedCodes.map((codes, index) => {
        const { item_code, element_code } = codes;

        // Filter data for the specific item_code and element_code combination
        const filteredData = data.filter(item =>
            matchesCode(item.item_code, item_code) && matchesCode(item.element_code, element_code)
        );

        if (!info && filteredData.length > 0) {
            const { area, year, value, ...otherInfo } = filteredData[0];
            info = otherInfo;
        }

        var dataPoints = labels.map((label, labelIndex) => {
            const dataItem = filteredData.find(item => item.year === label);

            if (!dataItem) {
                return { x: label, y: null, flagDescription: '' };
            }

            // Check exclusion conditions
            let excludedReason = null;
            if (isPoU && dataItem.unit === '%' && dataItem.value === '<2.5') {
                excludedReason = exclusionReasons.pou_low;
            } else if (isFS && dataItem.unit === '%' && dataItem.value === '<0.5') {
                excludedReason = exclusionReasons.fs_percent_low;
            } else if (isFS && dataItem.unit === '1000 No' && dataItem.value === '<100') {
                excludedReason = exclusionReasons.fs_number_low;
            }

            // If excluded, add to excluded array
            if (excludedReason) {
                const parsedValue = parseFloat(dataItem.value.replace('<', ''));
                if (!excluded[labelIndex]) {
                    excluded[labelIndex] = {
                        y: isNaN(parsedValue) ? 0 : parsedValue,
                        label: excludedReason
                    };
                }
                return { x: label, y: null, flagDescription: dataItem.flag_description || dataItem.FootNote || dataItem.footnote || dataItem.footnotes || "" };
            }

            return {
                x: label,
                y: formatNumber(parseFloat(dataItem.value)),
                flagDescription: dataItem.flag_description || dataItem.FootNote || dataItem.footnote || dataItem.footnotes || ""
            };
        });

        dataPoints = dataPoints.filter(point => point.y !== 'null' && point.y !== '' && point.y);

        const datasetLabel = filteredData.length > 0 ? filteredData[0].item : item_code;
        const unit = (filteredData.length > 0 && filteredData[0].unit) || fullInfo?.unit_by_item?.[item_code] || '';

        // Determine dataset properties based on unit
        const isPercentage = unit === '%';
        const yAxisID = isPercentage ? 'y-axis-2' : 'y-axis-1';

        // Use improved color selection for country datasets
        const borderColor = getDistinctiveColor(index, false);
        const backgroundColor = borderColor;
        const fill = !isPercentage;
        const type = isPercentage ? 'line' : 'bar';

        return {
            label: datasetLabel,
            yAxisID: yAxisID,
            data: dataPoints,
            borderColor: borderColor,
            backgroundColor: backgroundColor,
            fill: fill,
            type: type,
            stack: shouldStack ? 'stack-0' : (useSideBySideBars ? undefined : 'stack-0'),
            order: !isPercentage ? 2 : 1,
            unit: unit,
            // One dataset per configured code, whether or not the country reports it: the empty
            // ones stay for the table and the report generator, but they draw nothing and must
            // not sit in the legend. Same convention as the bar chart's noData.
            noData: dataPoints.length === 0,
        };
    });

    // Collect unique years for x-axis labels
    data.forEach(item => compareYearsSet.add(item.year));
    const compareLabels = Array.isArray(selectedYears) && selectedYears.length > 0
        ? [...selectedYears]
        : Array.from(compareYearsSet).sort();

    // Pair item_code and element_code arrays for dataset creation
    const compareCombinedCodes = useMultipleItemCodes
        ? item_code.map((code, index) => ({ item_code: code, element_code: element_code[index] || element_code[0] }))
        : element_code.map((code, index) => ({ item_code: item_code[0], element_code: code }));

    // collect unique comparison areas
    const compareAreasSet = new Set();
    data.forEach(item => { if (item.area_code_m49 !== countryCode) { compareAreasSet.add(item.area_code_m49); } });
    const compareAreas = Array.from(compareAreasSet);

    // create compareCombined as a combination of all combinations of the areas with the first compareCombinedCodes
    var compareCombined = [];
    compareAreas.forEach(area => {
        if (area !== countryCode) {
            for (let i = 0; i < compareCombinedCodes.length; i++) {
                compareCombined.push({ area: area, ...compareCombinedCodes[i] });
            }
        }
    });

    const compareDatasets = compareCombined.map((codes, index) => {
        const { item_code, element_code } = codes;

        // Filter data for the specific item_code and element_code combination
        const filteredData = data.filter(item =>
            matchesCode(item.item_code, item_code) && matchesCode(item.element_code, element_code) && matchesCode(item.area_code_m49, codes.area)
        );

        // collect info from the first dataset if not already set
        if (!info && filteredData.length > 0) {
            const { area, year, value, ...otherInfo } = filteredData[0];
            info = otherInfo;
        }


        var dataPoints = compareLabels.map(label => {
            const dataItem = filteredData.find(item => item.year === label);
            return dataItem
                ? {
                    x: label,
                    y: (isPoU && dataItem.unit === '%' && dataItem.value === '<2.5') ||
                        (isFS && dataItem.unit === '%' && dataItem.value === '<0.5') ||
                        (isFS && dataItem.unit === '1000 No' && dataItem.value === '<100')
                        ? null
                        : formatNumber(parseFloat(dataItem.value)),
                    flagDescription: dataItem.flag_description || dataItem.FootNote || dataItem.footnote || dataItem.footnotes || ""
                }
                : { x: label, y: null, flagDescription: '' };
        });

        dataPoints = dataPoints.filter(point => point.y !== 'null' && point.y !== '' && point.y);
        // sort dataPoints by x value

        const datasetLabel = filteredData.length > 0 ? filteredData[0].area : item_code;
        const unit = (filteredData.length > 0 && filteredData[0].unit) || fullInfo?.unit_by_item?.[item_code] || '';

        // Determine dataset properties based on unit
        const isPercentage = unit === '%';
        const yAxisID = isPercentage ? 'y-axis-2' : 'y-axis-1';

        // Improved color assignment for regional data
        const regionName = filteredData.length > 0 ? filteredData[0].area : '';
        let borderColor, backgroundColor;

        if (specialRegionColors[regionName]) {
            // Use special color for known regions
            borderColor = backgroundColor = specialRegionColors[regionName];
        } else {
            // Use extended regional palette, offset to avoid overlap with country colors
            const colorIndex = index % regionalColors.length;
            borderColor = backgroundColor = regionalColors[colorIndex];
        }

        const fill = !isPercentage;
        const type = isPercentage ? 'line' : 'bar';

        return {
            label: datasetLabel,
            yAxisID: yAxisID,
            data: dataPoints,
            borderColor: borderColor,
            backgroundColor: backgroundColor,
            fill: fill,
            type: type,
            stack: shouldStack ? 'stack-0' : (useSideBySideBars ? undefined : 'stack-0'),
            order: !isPercentage ? 2 : 1,
            unit: unit,
            // One dataset per configured code, whether or not the country reports it: the empty
            // ones stay for the table and the report generator, but they draw nothing and must
            // not sit in the legend. Same convention as the bar chart's noData.
            noData: dataPoints.length === 0,
        };
    });
    // Deduplicate by label to avoid duplicate legend entries when areas share names
    const dedupeByLabel = (arr) => {
        const seen = new Set();
        return arr.filter(ds => {
            const label = ds?.label || '';
            const key = `${label}__${ds?.yAxisID || ''}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    };

    const uniqueDatasets = dedupeByLabel(datasets);
    const uniqueCompareDatasets = dedupeByLabel(compareDatasets);

    // Allow mockData.js to force a unit when the underlying view doesn't provide one and no
    // per-item unit_by_item applies, so the data table's Unit column isn't left blank.
    if (fullInfo?.unit && !info?.unit) {
        info = { ...(info || {}), unit: fullInfo.unit };
    }

    return { labels, datasets: uniqueDatasets, compareDatasets: uniqueCompareDatasets, info, shouldStack, compareLabels, excluded, useSideBySideBars, groupByKey };
};



export const accumulateMetadata = (data) => {
    const groupedMetadata = data.reduce((acc, item) => {
        const group = item.metadata_group_label;

        if (!acc[group]) {
            acc[group] = [];
        }

        acc[group].push(item);
        return acc;
    }, {});

    return groupedMetadata;
};

// DATA CONVERSION FOR CHARTJS - Status Chart (True/False, 1/0)
// Yes/no indicators do not all speak the same language: the Food Systems Dashboard views
// spell the answer out as "Yes", while others store a plain 1 or 0. Reading the text with
// parseFloat alone yielded NaN, the value stayed at its 0 default and every country came out
// as "No". Returns null when there is nothing usable, so the caller keeps looking.
const readStatusValue = (raw) => {
    if (raw === null || raw === undefined) return null;

    const text = String(raw).trim().toLowerCase();
    if (text === "") return null;
    if (text === "yes" || text === "true") return 1;
    if (text === "no" || text === "false") return 0;

    const numeric = parseFloat(text);
    return Number.isNaN(numeric) ? null : numeric;
};

export const transformDataStatusChart = (data, element_code, item_code, countryCode, sex_code, group_code, isPoU, isFoodInsecurity, indicator_code, _unused = null, _unused2 = null, _selectedYears = null, fullInfo = null) => {
    const countryData = Array.isArray(data)
        ? data.filter((row) => Number(row.area_code_m49) === Number(countryCode))
        : [];

    if (!Array.isArray(element_code)) {
        element_code = element_code !== undefined && element_code !== null && element_code !== "" ? [element_code] : [];
    }
    element_code = element_code.filter(code => code !== null && code !== undefined && code !== "").map(code => {
        const str = String(code).trim();
        return isNaN(Number(str)) ? str : Number(str);
    });

    if (Array.isArray(item_code)) {
        item_code = item_code.filter(code => code !== null && code !== undefined && code !== "").map(code => {
            const str = String(code).trim();
            return isNaN(Number(str)) ? str : Number(str);
        });
    }

    let filtered = [];
    if (element_code.length > 0) {
        filtered = countryData.filter((row) =>
            element_code.some(code => matchesCode(row.element_code, code))
        );
    } else if (Array.isArray(item_code) && item_code.length > 0) {
        filtered = countryData.filter((row) =>
            item_code.some(code => matchesCode(row.item_code, code))
        );
    }

    // For status charts, we don't filter by year - we take the latest available value
    // or the first valid value if no year preference
    let statusValue = 0;
    let statusYear = null;
    let info = null;

    if (filtered.length > 0) {
        // Sort by year descending to get the latest value
        const sortedData = filtered.sort((a, b) => {
            const yearA = parseInt(a.year, 10) || 0;
            const yearB = parseInt(b.year, 10) || 0;
            return yearB - yearA;
        });

        // Get the first (latest) valid value
        for (const row of sortedData) {
            const value = readStatusValue(row.value);
            if (value !== null) {
                statusValue = value;
                statusYear = row.year;
                const { area, year, value: val, ...otherInfo } = row;
                info = otherInfo;
                break;
            }
        }
    }

    // Allow mockData.js to force a unit when the underlying view doesn't provide one,
    // so the data table's Unit column isn't left blank.
    if (fullInfo?.unit) {
        info = { ...(info || {}), unit: fullInfo.unit };
    }

    return {
        labels: ["Status"],
        datasets: [
            {
                label: info?.unit || "",
                data: [statusValue],
                backgroundColor: ["#58a14e"],
                borderColor: ["#58a14e"],
            },
        ],
        info: {
            ...(info || {}),
            statusValue,
        },
        // Read by the data table and its download, which lay a yes/no reading out as one answer
        // for the year it refers to rather than as a numeric series.
        isStatus: true,
        statusYear,
    };
};
